import { DottedPaths } from '@repo/types-client';

/**
 * Retrieves the value of a nested key from an object based on the provided path.
 * @function getKey
 * @param {any} context - The object from which to retrieve the key.
 * @param {string[]} paths - The path to the key in the object.
 * @returns {string} The value of the key.
 */
const getKey = (context: any, paths: string[]): string => {
  if (!context || !paths[0]) return '';
  if (paths.length === 1) {
    return context[paths[0]] ?? '';
  }

  const nextContext = context[paths[0]] ?? {};

  paths.shift();

  return getKey(nextContext, paths);
};

/**
 * Groups an array of objects by a specified key path.
 * @function groupBy
 * @template T - The type of the objects in the array.
 * @param {T[]} context - The array of objects to group.
 * @param {DottedPaths<T>} keyPath - The key path to group by.
 * @returns {Record<string, T[]>} An object where the keys are the values of the specified key path and the values are arrays of objects.
 */
export const groupBy = <T>(
  context: T[],
  keyPath: DottedPaths<T>,
): Record<string, T[]> => {
  if (!context) return {};

  const keys = [...(<string>keyPath).split('.')];

  return context.reduce((rv: Record<string, T[]>, x: any) => {
    const key = getKey(x, [...keys]);

    if (key !== undefined) {
      const str = key.toString();

      (rv[str] = rv[str] || []).push(x);
    }

    return rv;
  }, {});
};
